import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, Space, Tag, Modal, Form, Input, message, Popconfirm } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;


const SavedAudiences = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const { token } = useAuth();

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Rules', key: 'rules', width: 120, render: (_, r) => <Tag>rules: {Array.isArray(r.rules) ? r.rules.length : 0}</Tag> },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 200 },
    {
      title: 'Actions', key: 'actions', width: 220, render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => onEdit(record)}>Rename</Button>
          <Popconfirm title="Delete this audience?" onConfirm={() => onDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    const load = async () => {
      try {
        if (!token) return;
        setLoading(true);
        const res = await fetch('/api/custom-audiences', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load audiences');
        const data = await res.json();
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        message.error(e?.message || 'Failed to load audiences');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const onCreate = () => {
    form.resetFields();
    setOpen(true);
  };

  const onEdit = (record) => {
    form.setFieldsValue({ id: record.id, name: record.name });
    setOpen(true);
  };

  const onDelete = async (record) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/custom-audiences/${record.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete audience');
      setRows(prev => prev.filter(r => r.id !== record.id));
      message.success('Audience deleted');
    } catch (e) {
      message.error(e?.message || 'Delete failed');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (!token) throw new Error('Not authenticated');
      if (values.id) {
        const res = await fetch(`/api/custom-audiences/${values.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: values.name }),
        });
        if (!res.ok) throw new Error('Failed to rename audience');
        const updated = await res.json();
        setRows(prev => prev.map(r => r.id === updated.id ? updated : r));
        message.success('Audience renamed');
      } else {
        const res = await fetch('/api/custom-audiences', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: values.name, rules: [] }),
        });
        if (!res.ok) throw new Error('Failed to create audience');
        const created = await res.json();
        setRows(prev => [created, ...prev]);
        message.success('Audience created');
      }
      setOpen(false);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Audience' }, { title: 'Saved' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Saved Audiences</Title>
        <Space>
          <Button type="primary" onClick={onCreate}>New Audience</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Manage reusable audiences for targeting.</Paragraph>
        <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 10 }} />
      </Card>

      <Modal
        open={open}
        title="Audience"
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SavedAudiences;
